import { motion } from 'framer-motion';

import { TranscriptBlockQuote } from './transcript-block-quote';
import { capitalizeFirstLetter } from '../../../../../../../../../helpers/filterNames';
import TextWithFactReferences from '../../../../../../../../../screens/Chronos/transcribe/components/TextWithFactReferences';
import { getInconsistencyStatusLabel } from '../../../../../../../../../screens/Chronos/transcribe/utils/inconsistencyUtils';
import { Icon } from '../../../../../../../../components/icon';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import {
  TranscriptInconsistency,
  TranscriptInconsistencyLevel,
  TranscriptInconsistencyStatus,
} from '@/api/types/backend-dto-types';
import { cn } from '@/new/lib/utils';

interface TranscriptInconsistencyProps {
  inconsistency: TranscriptInconsistency;
  caseId: string;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  onScrollToBlock: (blockId: string) => void;
  isRecordingComplete?: boolean;
}

export const TranscriptInconsistencyComponent = ({
  inconsistency,
  caseId,
  isExpanded,
  onToggleExpansion,
  onScrollToBlock,
  isRecordingComplete = true,
}: TranscriptInconsistencyProps) => {
  const status = inconsistency.status || TranscriptInconsistencyStatus.Complete;

  return (
    <motion.div
      className="relative flex gap-3 pb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: 'easeOut',
      }}
    >
      {/* Timeline dot and connecting line */}
      <div
        className={`z-10 mt-4 mr-1 flex h-8 w-8 items-center justify-center rounded-full border ${
          inconsistency.level === TranscriptInconsistencyLevel.Severe
            ? 'border-red-400 bg-red-50 text-red-500 shadow-red-100'
            : inconsistency.level === TranscriptInconsistencyLevel.Minor
              ? 'border-amber-400 bg-amber-50 text-amber-500 shadow-amber-100'
              : 'border-gray-300 bg-gray-50 text-gray-400 shadow-gray-100'
        }`}
      >
        <Icon className="lucide--shield-alert" />
      </div>

      <div className="relative flex-1">
        <div className="absolute top-6.5 -left-2 h-0 w-0 border-t-[6px] border-r-[8px] border-b-[6px] border-t-transparent border-r-gray-200 border-b-transparent" />

        <div className="rounded-lg border border-gray-200 bg-white p-6 px-7 shadow-lg/2">
          {/* Title/Summary */}
          <div className="mb-3">
            <div className="text-sm font-medium text-gray-800">
              {inconsistency.summary ? (
                <>
                  <TextWithFactReferences text={inconsistency.summary} caseId={caseId} className="inline" />
                  {inconsistency.description && (
                    <button
                      onClick={() => {
                        if (!isExpanded) {
                          trackCustomEvent(TrackingEvents.ClickTranscriptInconsistencySeeMore);
                        }
                        onToggleExpansion();
                      }}
                      className="ml-1.5 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {isExpanded ? 'See less' : 'See more'}
                    </button>
                  )}
                </>
              ) : (
                <TextWithFactReferences text={inconsistency.description} caseId={caseId} className="inline" />
              )}
            </div>

            {/* Expanded description */}
            {isExpanded && inconsistency.summary && inconsistency.description && (
              <div className="mt-2 text-xs text-gray-600">
                <TextWithFactReferences
                  text={inconsistency.description}
                  caseId={caseId}
                  onFactClick={() => {
                    trackCustomEvent(TrackingEvents.ClickTranscriptInconsistencyReference);
                  }}
                />
              </div>
            )}
          </div>

          {/* Transcript quotes */}
          <div className="my-4 flex flex-col gap-2">
            {inconsistency.transcriptSubfact?.referencedBlocks?.map((block) => {
              return (
                <TranscriptBlockQuote
                  key={block.id}
                  block={block}
                  caseId={caseId}
                  transcriptId={inconsistency.transcriptSubfact?.transcriptId}
                  onClick={() => {
                    trackCustomEvent(TrackingEvents.ClickTranscriptInconsistencyReferenceBlock);
                    onScrollToBlock(block.id);
                  }}
                  isRecordingComplete={isRecordingComplete}
                />
              );
            })}
          </div>

          {/* Metadata */}
          <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
            <span>
              Level:{' '}
              <span
                className={cn(
                  inconsistency.level === TranscriptInconsistencyLevel.Severe
                    ? 'text-red-600'
                    : inconsistency.level === TranscriptInconsistencyLevel.Minor
                      ? 'text-amber-600'
                      : 'text-gray-500',
                  'font-medium',
                )}
              >
                {capitalizeFirstLetter(inconsistency.level)}
              </span>
            </span>
            <span>
              Evidence: <b>{inconsistency.evidenceFactIds.length}</b> documents
            </span>
            {status !== TranscriptInconsistencyStatus.Complete && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  status === TranscriptInconsistencyStatus.Reprocessing
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {getInconsistencyStatusLabel(status)}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
